import { SearchResultData } from "../../app/slices/SearchResultsSlice";
import { CharacterPartType } from "../../dto/CharacterPartDTO";
import { InteractionFactoryEntity } from "../../features/interaction_factory/InteractionFactory";
import SetCharacterPartsPreview from "../../features/previews/set_character_part/SetCharacterPartsPreview";
import SetCharacterPartsNodePreview from "../../features/sequence/node_previews/SetCharacterPartsNodePreview";
import { GetNewGuid } from "../../helpers/GuidHelper";
import { InteractionProperty } from "../InteractionProperty";
import Interaction, { InteractionType } from "./Interaction";
import { ValidationHelper } from "./../../features/validation/ValidationHelper";
import Take from "../Take";
import { Prop } from "../Prop";
import { LocalizedStringRecord } from "../../app/slices/TrainingLocalizationSlice";
import { Actor } from "../Actor";
import {
  compactSearchableStrings,
  SearchLookupContext,
} from "./SearchableInteractionValues";

export interface ISetCharacterParts extends Interaction {
  npcId: string;
  partDefinitions: ICharacterPartDefinition[];
}

export interface ICharacterPartDefinition {
  id: string;
  partType: CharacterPartType;
  partId: string;
  malePartId: string;
  femalePartId: string;
  variantId: string;
}

const requiredSetCharacterPartsProperties: InteractionProperty[] = [];

export const setCharacterPartsInteraction = (): InteractionFactoryEntity => {
  const view = <SetCharacterPartsPreview />;

  const create = (
    getKeys: (amount: number) => string[]
  ): ISetCharacterParts => {
    return {
      id: GetNewGuid(),
      name: "Set Character Parts",
      type: InteractionType.SetCharacterParts,
      npcId: "player",
      partDefinitions: [],
      properties: requiredSetCharacterPartsProperties,
    };
  };

  const migrate = (
    from: number,
    to: number,
    interaction: Interaction
  ): ISetCharacterParts => {
    return interaction as ISetCharacterParts;
  };

  const copy = (
    interaction: Interaction,
    getKeys: (amount: number) => string[],
    replaceKey: (from: string, to: string) => void
  ): ISetCharacterParts => {
    const casted = interaction as ISetCharacterParts;
    const setCharacterParts: ISetCharacterParts = {
      ...casted,
      id: GetNewGuid(),
      partDefinitions: casted.partDefinitions.map((part) => ({ ...part })),
      properties: casted.properties ? [...casted.properties] : [],
    };

    return setCharacterParts;
  };

  const validate = (interaction: Interaction): string[] => {
    const casted = interaction as ISetCharacterParts;
    const results: string[] = [];

    return results;
  };

  const validateErrors = (
    interaction: Interaction,
    parentTake: Take,
    currentTake: Take,
    actorPresets: Actor[],
    allPropPresets: Prop[],
    localizedStringRecords: LocalizedStringRecord
  ): string[] => {
    const casted = interaction as ISetCharacterParts;
    const results: string[] = [];

    const partTypes = casted.partDefinitions.map((part) => part.partType);
    const uniquePartTypes = [...new Set(partTypes)];

    if (casted.partDefinitions.length === 0) {
      results.push("Please select at least one character part to set");
    }

    if (uniquePartTypes.length !== partTypes.length) {
      results.push("You cannot have two parts with the same type");
    }

    return results;
  };

  const filter = (
    interaction: Interaction,
    takeName: string,
    getMatchingResults: (
      searchableStrings: string[],
      takeName: string,
      interaction: Interaction,
      filterType: string
    ) => SearchResultData[],
    _getValue: (key: string) => string,
    searchLookupContext?: SearchLookupContext,
  ): SearchResultData[] => {
    const converted = interaction as ISetCharacterParts;

    let searchableStrings: string[] = compactSearchableStrings(
      interaction.name,
      searchLookupContext?.getActorSearchTerms(converted.npcId) ??
        converted.npcId,
    );
    converted.partDefinitions.forEach((part) => {
      searchableStrings.push(CharacterPartType[part.partType]);
      searchableStrings.push(part.partId);
      searchableStrings.push(part.malePartId);
      searchableStrings.push(part.femalePartId);
      searchableStrings.push(part.variantId);
    });

    return getMatchingResults(
      compactSearchableStrings(searchableStrings),
      takeName,
      interaction,
      filterType
    );
  };

  const filterType: string = "Set Character Parts";

  return {
    View: view,
    NodeView: (nodeId: string, interaction: Interaction) => (
      <SetCharacterPartsNodePreview nodeId={nodeId} interaction={interaction} />
    ),
    FilterType: filterType,
    Create: create,
    Migrate: migrate,
    Copy: copy,
    Validate: validate,
    Filter: filter,
    ValidateErrors: validateErrors,
  };
};
